import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Avatar,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  TablePagination,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  ShoppingCart,
  Search,
  FilterList,
  Edit,
  Star,
  StarBorder,
  ViewList,
  ViewModule,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Customer {
  id: string;
  email: string;
  profile: any;
  orders: any[];
  totalSpent: number;
  tags: string[];
  isVip: boolean;
  created_at: string;
}

interface CustomersTabProps {
  customers: Customer[];
  loading: boolean;
  searchQuery: string;
  setSearchQuery: (val: string) => void;
  filterTag: string;
  setFilterTag: (val: string) => void;
  viewMode: 'list' | 'grid';
  setViewMode: (val: 'list' | 'grid') => void;
  page: number;
  setPage: (val: number) => void;
  rowsPerPage: number;
  setRowsPerPage: (val: number) => void;
  onToggleVip: (id: string) => void;
  onEditCustomer: (customer: Customer) => void;
}

const CustomersTab: React.FC<CustomersTabProps> = ({
  customers,
  loading,
  searchQuery,
  setSearchQuery,
  filterTag,
  setFilterTag,
  viewMode,
  setViewMode,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  onToggleVip,
  onEditCustomer,
}) => {
  const { t } = useTranslation();

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = !searchQuery || 
      customer.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.profile?.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      customer.profile?.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTag = !filterTag || customer.tags.includes(filterTag);
    
    return matchesSearch && matchesTag;
  });

  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      {/* Filters */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <TextField
          placeholder={t('admin.searchCustomers')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ flex: 1 }}
          fullWidth
          size="medium"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexDirection: { xs: 'row', sm: 'row' },
          width: { xs: '100%', sm: 'auto' }
        }}>
          <FormControl sx={{
            minWidth: { xs: '160px', sm: 200 },
            flex: { xs: 1, sm: 0 }
          }}>
            <InputLabel>{t('admin.filterByTag')}</InputLabel>
            <Select
              value={filterTag}
              label={t('admin.filterByTag')}
              onChange={(e) => setFilterTag(e.target.value as string)}
              startAdornment={<FilterList sx={{ mr: 1, color: 'text.secondary' }} />}
            >
              <MenuItem value="">{t('admin.allCustomers')}</MenuItem>
              <MenuItem value="VIP">{t('admin.vip')}</MenuItem>
              <MenuItem value="Frequent Buyer">{t('admin.frequentBuyer')}</MenuItem>
            </Select>
          </FormControl>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, newMode) => newMode && setViewMode(newMode)}
            size="small"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            <ToggleButton value="list">
              <ViewList />
            </ToggleButton>
            <ToggleButton value="grid">
              <ViewModule />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* Customer List/Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : viewMode === 'list' ? (
        <>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 600, sm: 750 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('admin.customer')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{t('admin.contact')}</TableCell>
                  <TableCell align="right">{t('admin.totalSpent')}</TableCell>
                  <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>{t('nav.orders')}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{t('admin.tags')}</TableCell>
                  <TableCell align="center">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCustomers.map((customer) => (
                  <TableRow key={customer.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Avatar sx={{
                          bgcolor: customer.isVip ? '#ff0080' : 'rgba(0,212,255,0.2)',
                          width: { xs: 32, sm: 40 },
                          height: { xs: 32, sm: 40 }
                        }}>
                          <Person fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                          >
                            {customer.profile?.full_name || 'Customer'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              fontSize: { xs: '0.7rem', sm: '0.75rem' },
                              display: { xs: 'none', sm: 'block' }
                            }}
                          >
                            {customer.profile?.company_name || 'Individual'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">
                            {customer.email || 'N/A'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="caption">
                            {customer.profile?.phone || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body1"
                        fontWeight={700}
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        €{customer.totalSpent.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      <Badge badgeContent={customer.orders.length} color="primary">
                        <ShoppingCart fontSize="small" />
                      </Badge>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {customer.tags.map((tag) => (
                          <Chip
                            key={tag}
                            label={tag}
                            size="small"
                            sx={{
                              bgcolor: tag === 'VIP' ? 'rgba(255,0,128,0.2)' : 'rgba(0,212,255,0.2)',
                              color: tag === 'VIP' ? '#ff0080' : '#00d4ff',
                              border: tag === 'VIP' ? '1px solid rgba(255,0,128,0.3)' : '1px solid rgba(0,212,255,0.3)',
                              fontSize: { xs: '0.7rem', sm: '0.75rem' }
                            }}
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => onToggleVip(customer.id)}
                        sx={{ color: customer.isVip ? '#ff0080' : 'text.secondary' }}
                      >
                        {customer.isVip ? <Star /> : <StarBorder />}
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => onEditCustomer(customer)}
                        sx={{ color: '#00d4ff' }}
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredCustomers.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      ) : (
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: { 
            xs: '1fr', 
            sm: 'repeat(2, 1fr)', 
            md: 'repeat(3, 1fr)',
            lg: 'repeat(4, 1fr)',
          }, 
          gap: 3 
        }}>
          {paginatedCustomers.map((customer) => (
            <Card
              key={customer.id}
              sx={{
                bgcolor: 'background.paper',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                transition: 'all 0.3s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 4px 12px rgba(0,212,255,0.1)',
                },
              }}
            >
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Avatar sx={{ bgcolor: customer.isVip ? '#ff0080' : 'rgba(0,212,255,0.2)' }}>
                    <Person />
                  </Avatar>
                  <IconButton
                    size="small"
                    onClick={() => onToggleVip(customer.id)}
                    sx={{ color: customer.isVip ? '#ff0080' : 'text.secondary' }}
                  >
                    {customer.isVip ? <Star /> : <StarBorder />}
                  </IconButton>
                </Box>
                <Typography variant="h6" gutterBottom>
                  {customer.profile?.full_name || 'Customer'}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {customer.email}
                </Typography>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('admin.totalSpent')}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    €{customer.totalSpent.toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {customer.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        bgcolor: tag === 'VIP' ? 'rgba(255,0,128,0.2)' : 'rgba(0,212,255,0.2)',
                        color: tag === 'VIP' ? '#ff0080' : '#00d4ff',
                        border: tag === 'VIP' ? '1px solid rgba(255,0,128,0.3)' : '1px solid rgba(0,212,255,0.3)',
                      }}
                    />
                  ))}
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default CustomersTab;